<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import BaseDialog from "@/stories/dialogs/BaseDialog.vue";
import Button from "@/stories/buttons/Button/Button.vue";
import { type ConfirmationGroup } from "@/interfaces";
import type { ConfirmationDetail } from "@/types";

const TEXT_KEYS: Record<string, ConfirmationGroup> = {
  default: {
    title: "confirmation.title",
    description: "confirmation.description",
    cancelText: "templates.cancel",
    confirmText: "confirmation.confirm",
  },
  notShared: {
    title: "templates.visible_for_department_group",
    description: "templates.title_not_visible_departments_groups",
    cancelText: "templates.cancel",
    confirmText: "global.save",
  },
};

const message = ref<ConfirmationDetail | undefined>(undefined);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onMessage = (event: any) => {
  message.value = event.detail;
};

const closeMessage = () => {
  if (typeof message.value?.onCancel === "function") {
    message.value?.onCancel();
  }
  message.value = undefined;
};

const confirm = () => {
  if (typeof message.value?.onConfirm === "function") {
    message.value?.onConfirm();
  }

  message.value = undefined;
};

onMounted(() => {
  document.addEventListener("custom-confirmation", onMessage);
});

onBeforeUnmount(() => {
  document.removeEventListener("custom-confirmation", onMessage);
});

const activeGroup = computed<ConfirmationGroup | undefined>(() => {
  if (!message.value) {
    return;
  }

  if (message.value.custom) {
    return message.value.custom;
  }

  return TEXT_KEYS[message.value.textType || "default"];
});

const activeIcon = computed(() => {
  return activeGroup.value?.icon || "fa-circle-info";
});
</script>

<script lang="ts">
export const confirmation = ({
  onConfirm,
  onCancel,
  variant = "error",
  textType = "default",
  custom,
}: ConfirmationDetail) => {
  document.dispatchEvent(
    new CustomEvent("custom-confirmation", {
      detail: { onConfirm, onCancel, textType, variant, custom },
    }),
  );
};

export const confirmationSync = async (detail: ConfirmationDetail) => {
  return new Promise((resolve) => {
    confirmation({
      ...detail,
      onConfirm() {
        resolve(0);
      },
    });
  });
};
</script>

<template>
  <BaseDialog v-if="!!message && !!activeGroup" @on-close="$emit('close')">
    <div
      class="overflow-hidden w-full text-center items-center laptop:w-[28.125rem] pt-12 pb-8 px-14"
    >
      <div class="w-full flex justify-center">
        <div
          class="flex justify-center items-center size-[3.75rem] rounded-full bg-darkOcean100 mb-3"
        >
          <i class="fa text-[1.6875rem]" :class="activeIcon" />
        </div>
      </div>
      <div class="relative h800 text-neutral1000">
        <div class="flex items-center justify-center">
          {{ $t(activeGroup.title) }}
        </div>
      </div>
      <div class="b300 py-3 text-center">
        {{ $t(activeGroup.description) }}
      </div>
    </div>
    <div class="flex justify-between gap-3 py-4 px-6">
      <Button
        :text="$t(activeGroup.cancelText)"
        type="grey"
        @click="closeMessage"
        :fullWidth="true"
      />
      <Button
        :fullWidth="true"
        :text="$t(activeGroup.confirmText)"
        :type="message?.variant || 'error'"
        @click="confirm"
      />
    </div>
  </BaseDialog>
</template>
